'use client';

import { Button, Input, Label } from '@routly/ui';
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useUpdateUser } from '@/hooks/useUserActions';
import type { User } from '@/lib/types';

/**
 * Changing a person's name or address.
 *
 * The console could set a role, deactivate an account and close its sessions,
 * but not correct the two fields people actually get wrong: a misspelt name and
 * an address that changed when someone married or the company moved domain.
 * `PATCH /v1/admin/users/:id` has always accepted both; nothing called it.
 *
 * Role is deliberately not here. It has its own control in the row, and it is
 * the change a security review looks for — burying it inside a general edit
 * form would make it one field among several rather than a decision.
 */
export function EditUserDialog({ user }: { user: User }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user.name ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const updateUser = useUpdateUser();

  // The row behind the dialog refreshes while it is closed; reopening should
  // show what is stored now, not what was stored when the page loaded.
  useEffect(() => {
    if (open) {
      setName(user.name ?? '');
      setEmail(user.email ?? '');
    }
  }, [open, user.name, user.email]);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const isValid = trimmedName !== '' && /\S+@\S+\.\S+/.test(trimmedEmail);
  const isChanged = trimmedName !== (user.name ?? '') || trimmedEmail !== (user.email ?? '');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || !isChanged || updateUser.isPending) return;

    try {
      await updateUser.mutateAsync({
        id: user.id,
        // Only what changed: sending an unchanged email would still write an
        // audit entry claiming it was edited.
        ...(trimmedName !== (user.name ?? '') ? { name: trimmedName } : {}),
        ...(trimmedEmail !== (user.email ?? '') ? { email: trimmedEmail } : {}),
      });
      toast.success('Saved');
      setOpen(false);
    } catch (caught) {
      toast.error('Could not save', {
        description: caught instanceof Error ? caught.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" aria-label={`Edit ${user.email ?? user.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Edit account</DialogTitle>
            <DialogDescription>
              Changing the address changes what this person signs in with.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={!isValid || !isChanged || updateUser.isPending}>
              {updateUser.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
